import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import "./LegalApp.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaFileWord } from "react-icons/fa";

const UploadChargeSheet = ({ API_URL }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const fir = location.state?.fir ?? null;

  const [language, setLanguage] = useState("English");
  const [selectedFile, setSelectedFile] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [showOtherLanguagePopup, setShowOtherLanguagePopup] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const wordFileInputRef = useRef(null);

  const handleCancel = (e) => {
    e.preventDefault();
    navigate(-1);
  };

  const handleLanguageChange = (event) => {
    if (event.target.value === "Other") {
      setShowOtherLanguagePopup(true);
    } else {
      setLanguage(event.target.value);
      setShowOtherLanguagePopup(false);
    }
  };

  const handleLanguageChangePopup = () => {
    setShowOtherLanguagePopup(false);
  };

  const handleDocxSelected = (event) => {
    setErrorMessage(null);

    const file = event.target.files?.[0];
    if (!file) return;

    const isDocx =
      file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      file.name.toLowerCase().endsWith(".docx");

    if (!isDocx) {
      setSelectedFile(null);
      setErrorMessage("Please select a .docx Word document only.");
      if (wordFileInputRef.current) wordFileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (wordFileInputRef.current) wordFileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setErrorMessage(null);

    if (!selectedFile) {
      setErrorMessage("Please select the charge sheet (.docx) to proceed.");
      return;
    }

    setIsLoading(true);
    const jwt = localStorage.getItem("jwtToken");

    const formData = new FormData();
    formData.append("id", fir?.id);
    formData.append("language", language);

    // ✅ Include the file in the API call
    // Adjust the field name if your backend expects a different key.
    formData.append("file", selectedFile, selectedFile.name);

    try {
      const uri = "/fir/chargesheet"; // now always docx upload
      await axios.post(API_URL + uri, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${jwt}`,
        },
      });

      setShowConfirmationPopup(true);
    } catch (error) {
      const defaultErrorMessage =
        "Error processing request. Please try again later.";
      if (error.response?.status === 401) {
        setErrorMessage("Your session has expired. Please log in again.");
      } else {
        setErrorMessage(
          error.response?.data?.message || error.message || defaultErrorMessage
        );
      }
      console.error("Error submitting the form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const controlStyle = {
    width: "100%",
    height: "44px",
    boxSizing: "border-box",
  };

  const fileInputStyle = {
    ...controlStyle,
    padding: "8px 10px",       // helps normalize file input height
    lineHeight: "28px",
  };


  return (
    <div>
      <Navbar />

      <div className="form-container" style={{ marginTop: "100px" }}>
        <div style={{ alignContent: "center", textAlign: "center" }}>
          <h2>Verify Charge Sheet</h2>
        </div>
        <br />

        <form
          className="responsive-form"
          onSubmit={handleSubmit}
          noValidate
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
          }}
        >
          <div style={{ width: "100%" }}>
            {/* ✅ Language + File selector on the same row */}
            <div
              style={{
                width: "100%",
                display: "flex",
                gap: "16px",
                // alignItems: "flex-start",
                // alignContent: "center",
                justifyContent: "space-between",
                flexWrap: "nowrap",
                boxSizing: "border-box",
              }}
            >
              {/* Language */}
              <div className="form-group">
                <label htmlFor="document-type">Charge Sheet Language</label>
                <select
                  id="document-type"
                  name="document-type"
                  className="upload-select"
                  onChange={handleLanguageChange}
                  value={language}
                // style={controlStyle}
                >
                  <option value="English">English</option>
                  {/* <option value="Hindi">Hindi</option>
                  <option value="Gujarati">Gujarati</option>
                  <option value="Marathi">Marathi</option>
                  <option value="Bengali">Bengali</option>
                  <option value="Telugu">Telugu</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Kannada">Kannada</option>
                  <option value="Malayalam">Malayalam</option>
                  <option value="Punjabi">Punjabi</option> */}
                  <option value="Other">Other Languages</option>
                </select>
              </div>

              {/* File selector */}
              <div className="form-group" style={{marginTop: '3px'}}>
                <label htmlFor="chargesheet-docx">Charge Sheet (.docx)</label>

                {/* Hidden real file input */}
                <input
                  ref={wordFileInputRef}
                  id="chargesheet-docx"
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleDocxSelected}
                  disabled={isLoading || language === "Other"}
                  style={{ display: "none" }}
                />

                {/* Visible "filename" input + Browse button */}
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <input
                    type="text"
                    value={selectedFile ? selectedFile.name : ""}
                    readOnly
                    placeholder="No file selected"
                    disabled={isLoading || language === "Other"}
                    style={{
                      flex: 1,
                      height: "40px",
                      boxSizing: "border-box",
                      padding: "8px 10px",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                      minWidth: "260px",
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => wordFileInputRef.current?.click()}
                    disabled={isLoading || language === "Other"}
                    style={{
                      height: "40px",
                      padding: "0 14px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      background: "white",
                      cursor: isLoading || language === "Other" ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      whiteSpace: "nowrap",
                    }}
                    title="Browse and select a Word (.docx) file"
                  >
                    <FaFileWord style={{ fontSize: "18px", color: "#2B579A" }} />
                    Browse
                  </button>
                </div>
              </div>

            </div>

            <br />
            <br />

            {/* ✅ Submit + Cancel row (removed File button and removed textarea entirely) */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                marginTop: "20px",
                position: "relative",
                gap: "12px",
              }}
            >
              <div />

              <button
                type="submit"
                className="submit-button"
                style={{ fontWeight: "bold" }}
                disabled={isLoading || language === "Other" || !selectedFile}
                hidden={errorMessage}
              >
                {isLoading ? <div className="spinner"></div> : "Submit"}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  borderBottom: "1px solid #000000",
                  padding: "4px 0 2px 0",
                  fontWeight: "bold",
                  cursor: "pointer",
                  marginRight: "20px",
                }}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>

            <div hidden={!isLoading} style={{ marginTop: "10px", textAlign: "center" }}>
              <p style={{ fontSize: "14px" }}>
                <strong>
                  Please wait. AI Agent is processing the request – this may take
                  about a minute depending on the complexity of the charge sheet.
                </strong>
              </p>
            </div>
          </div>
        </form>

        {errorMessage && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Error</h2>
              <div
                className="error-message"
                style={{ marginBottom: "15px", fontSize: "16px", color: "#333" }}
              >
                <strong>{errorMessage}</strong>
              </div>
              <button
                className="error-ok-button"
                onClick={() => setErrorMessage(null)}
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  color: "white",
                  backgroundColor: "#007bff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {showOtherLanguagePopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Support for more languages coming soon!</h3>
              <p>
                In the meantime, please translate the Charge Sheet into English using
                tools like{" "}
                <a
                  href="https://chat.openai.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ChatGPT
                </a>{" "}
                and{" "}
                <a
                  href="https://openpdf.com/lp/translate-pdf.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  OpenPDF
                </a>{" "}
                and upload the translated FIR as a PDF.
              </p>
              <p>
                <strong>For assistance, please email us at contact@legalapp.in.</strong>
              </p>
              <div className="popup-buttons">
                <button className="submit-popup-button" onClick={handleLanguageChangePopup}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmationPopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Charge Sheet Submitted Successfully</h3>
              <p>Our AI assistant is working on verifying the charge sheet.</p>
              <p>This may take a little time.</p>
              <p>You can continue using the app and check back later for updates.</p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={() => navigate("/fir/dashboard", { state: { API_URL, fir } })}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default UploadChargeSheet;
